import { ArrowDownUp, Pencil, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import {
  RentingFeesInfoCell,
  type RentingFeesInfoCellRow,
} from "@/routes/base-data/processes/qpm/_components/renting-fees-info-cell";

type SortField = "name" | "monitorType" | "basePrice" | "updatedAt";
type SortDirection = "asc" | "desc";

export type AdditionalOverviewRow = {
  id: string;
  name: string;
  monitorType: MonitorType;
  description?: string;
  basePrice: number;
  updatedAt?: string;
  rentingFees: RentingFeesInfoCellRow[];
  onEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
};

export function AdditionalOverviewTable({
  rows,
  monitorType,
}: {
  rows: AdditionalOverviewRow[];
  monitorType: MonitorType;
}) {
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const showRentingColumn = monitorType === MonitorType.IoT;

  const sortedRows = useMemo(
    () =>
      [...rows].sort((left, right) =>
        compareRows(left, right, sortField, sortDirection),
      ),
    [rows, sortDirection, sortField],
  );

  return (
    <div className="overflow-x-auto">
      <Table className="min-w-[980px]">
        <TableHeader>
          <TableRow>
            <SortableHead
              active={sortField === "name"}
              direction={sortDirection}
              onClick={() =>
                updateSort(
                  "name",
                  sortField,
                  sortDirection,
                  setSortField,
                  setSortDirection,
                )
              }
            >
              {t("Name")}
            </SortableHead>
            <SortableHead
              active={sortField === "monitorType"}
              direction={sortDirection}
              onClick={() =>
                updateSort(
                  "monitorType",
                  sortField,
                  sortDirection,
                  setSortField,
                  setSortDirection,
                )
              }
            >
              {t("Monitor-Typ")}
            </SortableHead>
            <TableHead>{t("Katalogtext")}</TableHead>
            <SortableHead
              active={sortField === "basePrice"}
              direction={sortDirection}
              className="text-right"
              onClick={() =>
                updateSort(
                  "basePrice",
                  sortField,
                  sortDirection,
                  setSortField,
                  setSortDirection,
                )
              }
            >
              {t("Preis")}
            </SortableHead>
            {showRentingColumn ? <TableHead>{t("Miete")}</TableHead> : null}
            <SortableHead
              active={sortField === "updatedAt"}
              direction={sortDirection}
              className="text-right"
              onClick={() =>
                updateSort(
                  "updatedAt",
                  sortField,
                  sortDirection,
                  setSortField,
                  setSortDirection,
                )
              }
            >
              {t("Aktualisiert am")}
            </SortableHead>
            <TableHead className="text-right">{t("Aktionen")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="font-medium">{row.name}</TableCell>
              <TableCell>
                {MonitorTypeUtil.toLongString(row.monitorType)}
              </TableCell>
              <TableCell className="max-w-[420px] truncate">
                {row.description || "-"}
              </TableCell>
              <TableCell className="text-right">
                {row.basePrice.toFixed(2)}
              </TableCell>
              {showRentingColumn ? (
                <TableCell>
                  <RentingFeesInfoCell rows={row.rentingFees} />
                </TableCell>
              ) : null}
              <TableCell className="text-right">
                {formatDateTime(row.updatedAt)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={row.onEdit}
                    disabled={row.disabled}
                    aria-label={t("Material-Zusatz bearbeiten")}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={row.onDelete}
                    disabled={row.disabled}
                    aria-label={t("Material-Zusatz entfernen")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {sortedRows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={showRentingColumn ? 7 : 6}
                className="text-muted-foreground h-24 text-center"
              >
                {t("Keine Material-Zusätze hinterlegt")}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}

function SortableHead({
  active,
  direction,
  className,
  onClick,
  children,
}: {
  active: boolean;
  direction: SortDirection;
  className?: string;
  onClick: () => void;
  children: string;
}) {
  return (
    <TableHead className={className}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="-ml-3 h-8 px-3"
        onClick={onClick}
      >
        {children}
        <ArrowDownUp
          className={`ml-2 h-3.5 w-3.5 ${active ? "opacity-100" : "opacity-40"}`}
        />
        <span className="sr-only">
          {direction === "asc" ? t("Aufsteigend") : t("Absteigend")}
        </span>
      </Button>
    </TableHead>
  );
}

function updateSort(
  nextField: SortField,
  currentField: SortField,
  currentDirection: SortDirection,
  setField: (field: SortField) => void,
  setDirection: (direction: SortDirection) => void,
) {
  if (nextField === currentField) {
    setDirection(currentDirection === "asc" ? "desc" : "asc");
    return;
  }
  setField(nextField);
  setDirection("asc");
}

function compareRows(
  left: AdditionalOverviewRow,
  right: AdditionalOverviewRow,
  sortField: SortField,
  sortDirection: SortDirection,
) {
  const direction = sortDirection === "asc" ? 1 : -1;
  if (sortField === "monitorType") {
    return (
      MonitorTypeUtil.toLongString(left.monitorType).localeCompare(
        MonitorTypeUtil.toLongString(right.monitorType),
        undefined,
        { sensitivity: "base" },
      ) * direction
    );
  }
  if (sortField === "basePrice") {
    return (left.basePrice - right.basePrice) * direction;
  }
  if (sortField === "updatedAt") {
    return compareTimestamps(left.updatedAt, right.updatedAt) * direction;
  }
  return (
    left.name.localeCompare(right.name, undefined, {
      sensitivity: "base",
    }) * direction
  );
}

function compareTimestamps(left?: string, right?: string): number {
  const leftTime = left ? Date.parse(left) : Number.MAX_SAFE_INTEGER;
  const rightTime = right ? Date.parse(right) : Number.MAX_SAFE_INTEGER;
  if (leftTime === rightTime) {
    return 0;
  }
  return leftTime < rightTime ? -1 : 1;
}

function formatDateTime(value?: string): string {
  if (!value) {
    return "-";
  }
  return `${formatDate(value)} ${formatTime(value)}`;
}
